import * as React from 'react';
import { cn } from '@/lib/utils';

// --- Campo de texto --------------------------------------------------------

export const Campo = React.forwardRef<HTMLInputElement, React.InputHTMLAttributes<HTMLInputElement>>(
  function Campo({ className, ...props }, ref) {
    return (
      <input
        ref={ref}
        className={cn(
          'min-h-toque w-full rounded-lg border border-borda bg-superficie px-3.5 text-sm text-texto shadow-xs',
          'transition-colors placeholder:text-textoSuave hover:border-bordaForte',
          'focus:border-primaria focus:outline-none focus:ring-4 focus:ring-primaria/12',
          'disabled:cursor-not-allowed disabled:bg-superficie2 disabled:opacity-60',
          className,
        )}
        {...props}
      />
    );
  },
);

/** Campo com ícone à esquerda — o padrão de busca/filtro do sistema. */
export function CampoComIcone({
  icone,
  sufixo,
  className,
  classeCampo,
  ...props
}: React.InputHTMLAttributes<HTMLInputElement> & {
  icone: React.ReactNode;
  sufixo?: React.ReactNode | undefined;
  classeCampo?: string | undefined;
}): JSX.Element {
  return (
    <div className={cn('relative', className)}>
      <span className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-textoSuave">
        {icone}
      </span>
      <Campo className={cn('pl-10', sufixo ? 'pr-12' : '', classeCampo)} {...props} />
      {sufixo ? (
        <span className="absolute right-2.5 top-1/2 -translate-y-1/2">{sufixo}</span>
      ) : null}
    </div>
  );
}

// --- Cartão ----------------------------------------------------------------

export function Cartao({
  className,
  interativo = false,
  ...props
}: React.HTMLAttributes<HTMLDivElement> & { interativo?: boolean }): JSX.Element {
  return <div className={cn(interativo ? 'cartao-interativo' : 'cartao', className)} {...props} />;
}

export function CabecalhoCartao({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>): JSX.Element {
  return (
    <div
      className={cn(
        'flex items-center justify-between gap-3 border-b border-borda px-5 py-3.5',
        className,
      )}
      {...props}
    />
  );
}

export function TituloCartao({
  className,
  ...props
}: React.HTMLAttributes<HTMLHeadingElement>): JSX.Element {
  return (
    <h2
      className={cn('text-[14.5px] font-semibold tracking-[-0.01em] text-texto', className)}
      {...props}
    />
  );
}

export function CorpoCartao({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>): JSX.Element {
  return <div className={cn('p-5', className)} {...props} />;
}

// --- Cabeçalho de página ---------------------------------------------------

/**
 * Toda tela abre com o mesmo bloco: sobrelinha (onde estou), título grande
 * e ações à direita. Consistência é o que faz parecer produto, não coleção
 * de páginas.
 */
export function CabecalhoPagina({
  sobrelinha,
  titulo,
  descricao,
  icone,
  acoes,
  className,
}: {
  sobrelinha?: React.ReactNode | undefined;
  titulo: React.ReactNode;
  descricao?: React.ReactNode | undefined;
  icone?: React.ReactNode | undefined;
  acoes?: React.ReactNode | undefined;
  className?: string | undefined;
}): JSX.Element {
  return (
    <header className={cn('flex flex-wrap items-start justify-between gap-3', className)}>
      <div className="flex min-w-0 items-start gap-3">
        {icone ? (
          <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-primaria-suave text-primaria ring-1 ring-inset ring-primaria/15">
            {icone}
          </span>
        ) : null}
        <div className="min-w-0">
          {sobrelinha ? <p className="rotulo mb-1">{sobrelinha}</p> : null}
          <h1 className="text-[22px] font-bold leading-tight tracking-[-0.02em] text-texto">
            {titulo}
          </h1>
          {descricao ? (
            <p className="mt-1 max-w-2xl text-[13px] leading-relaxed text-textoFraco">{descricao}</p>
          ) : null}
        </div>
      </div>
      {acoes ? <div className="flex flex-wrap items-center gap-2">{acoes}</div> : null}
    </header>
  );
}

// --- Badge / Pílula --------------------------------------------------------

export function Badge({
  className,
  ...props
}: React.HTMLAttributes<HTMLSpanElement>): JSX.Element {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-2xs font-semibold',
        className,
      )}
      {...props}
    />
  );
}

export function Pilula({
  className,
  tom = 'neutro',
  ...props
}: React.HTMLAttributes<HTMLSpanElement> & {
  tom?: 'neutro' | 'marca' | 'ok' | 'alerta' | 'erro';
}): JSX.Element {
  const tons: Record<string, string> = {
    neutro: 'bg-superficie2 text-textoFraco ring-borda',
    marca: 'bg-primaria-suave text-primaria ring-primaria/20',
    ok: 'bg-status-livreFundo text-status-livre ring-status-livre/25',
    alerta: 'bg-status-atencaoFundo text-status-atencao ring-status-atencao/25',
    erro: 'bg-status-divergenciaFundo text-status-divergencia ring-status-divergencia/25',
  };
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-2xs font-bold ring-1 ring-inset',
        tons[tom],
        className,
      )}
      {...props}
    />
  );
}

// --- KPI -------------------------------------------------------------------

/**
 * Cartão de indicador. Rótulo pequeno, número grande tabular, nota e barra
 * fina — o padrão que o cliente aprovou no getSefaz, com mais acabamento.
 */
export function Kpi({
  rotulo,
  valor,
  nota,
  barra,
  icone,
  tom = 'marca',
  className,
  children,
}: {
  rotulo: React.ReactNode;
  valor: React.ReactNode;
  nota?: React.ReactNode | undefined;
  barra?: number | undefined;
  icone?: React.ReactNode | undefined;
  tom?: 'marca' | 'ok' | 'alerta' | 'erro' | 'neutro';
  className?: string | undefined;
  children?: React.ReactNode | undefined;
}): JSX.Element {
  const tons = {
    marca: { texto: 'text-primaria', fundo: 'bg-primaria-suave', barra: 'gradiente-marca' },
    ok: { texto: 'text-status-livre', fundo: 'bg-status-livreFundo', barra: 'bg-status-livre' },
    alerta: {
      texto: 'text-status-atencao',
      fundo: 'bg-status-atencaoFundo',
      barra: 'bg-status-atencao',
    },
    erro: {
      texto: 'text-status-divergencia',
      fundo: 'bg-status-divergenciaFundo',
      barra: 'bg-status-divergencia',
    },
    neutro: { texto: 'text-textoFraco', fundo: 'bg-superficie2', barra: 'bg-bordaForte' },
  }[tom];

  return (
    <div className={cn('cartao overflow-hidden p-4', className)}>
      <div className="flex items-start justify-between gap-2">
        <p className="rotulo">{rotulo}</p>
        {icone ? (
          <span className={cn('grid h-8 w-8 place-items-center rounded-lg', tons.fundo, tons.texto)}>
            {icone}
          </span>
        ) : null}
      </div>
      <p className="tabular mt-2 text-[30px] font-bold leading-none tracking-[-0.03em]">{valor}</p>
      {nota ? <p className="mt-1.5 text-xs text-textoFraco">{nota}</p> : null}
      {barra !== undefined ? (
        <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-superficie3">
          <span
            className={cn('block h-full rounded-full transition-[width] duration-500', tons.barra)}
            style={{ width: `${Math.max(2, Math.min(100, barra))}%` }}
          />
        </div>
      ) : null}
      {children}
    </div>
  );
}

// --- Skeleton --------------------------------------------------------------

export function Esqueleto({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>): JSX.Element {
  return (
    <div
      aria-hidden
      className={cn(
        'relative overflow-hidden rounded-lg bg-superficie2',
        'after:absolute after:inset-0 after:-translate-x-full after:animate-[brilho_1.6s_infinite]',
        'after:bg-gradient-to-r after:from-transparent after:via-superficie/80 after:to-transparent',
        className,
      )}
      {...props}
    />
  );
}

export function LinhasEsqueleto({ linhas = 3 }: { linhas?: number }): JSX.Element {
  return (
    <div className="space-y-2">
      {Array.from({ length: linhas }, (_, i) => (
        <Esqueleto key={i} className="h-4" style={{ width: `${100 - i * 12}%` }} />
      ))}
    </div>
  );
}

// --- Separador -------------------------------------------------------------

export function Separador({ className }: { className?: string | undefined }): JSX.Element {
  return <div role="separator" className={cn('h-px w-full bg-borda', className)} />;
}
